import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

import { userQueryKeys } from "./query-keys";

import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";
import { DeleteUserRequest } from "@/types/request/DeleteUserRequest";
import { ValidationResponse } from "@/types/response/ValidationResponse";

/**
 * Hook to delete a user.
 *
 * @returns Mutation object for deleting a user
 *
 * @example
 * ```tsx
 * const deleteUser = useDeleteUserMutation();
 * deleteUser.mutate({ id: "user-123" });
 * ```
 */
export function useDeleteUserMutation() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<
    AxiosResponse,
    AxiosError<ValidationResponse>,
    DeleteUserRequest
  >({
    mutationFn: (deleteUserRequest: DeleteUserRequest) => {
      return instance.delete(`/api/v2/Users/${deleteUserRequest.id}`);
    },
    onSuccess: () => {
      showToast(t("success.deleted", { entity: "User" }), "success");
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.all,
      });
    },
    onError: () => {
      showToast(t("delete.failed", { entity: "User" }), "error");
    },
  });
}
